import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './AuditTable.module.css';

/**
 * EAA-1821: the actor column renders a Keycloak `sub` — an opaque id that
 * dominates the row at full length but that support needs to grep in full.
 * Truncated to 8 chars for display; the FULL value stays reachable two ways:
 * the native `title` tooltip on hover, and this button, which copies it and
 * swaps its icon (+ an `aria-label`/sr-only text change) for ~1.5s as a
 * visible, keyboard-reachable confirmation — no toast dependency, so the
 * feedback sits right where the user's focus already is.
 */
export function CopyableId({ value }: { value: string }) {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const timeoutRef = useRef<number | undefined>(undefined);

    useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

    const short = value.length > 8 ? `${value.slice(0, 8)}…` : value;

    async function handleCopy() {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = window.setTimeout(() => setCopied(false), 1500);
        } catch {
            // EAA-1821: clipboard API can reject (insecure context, denied
            // permission, unsupported browser) — fail silently rather than
            // throwing inside a table cell. The full value is still visible
            // via `title` even if the copy itself didn't go through.
        }
    }

    return (
        <button
            type="button"
            className={styles.copy}
            onClick={handleCopy}
            title={value}
            aria-label={
                copied
                    ? t('console.audit.copy.copiedAriaLabel', { value })
                    : t('console.audit.copy.copyAriaLabel', { value })
            }
        >
            <code className={styles.copyCode}>{short}</code>
            <span className={styles.copyIcon} aria-hidden="true">
                {copied ? (
                    <svg viewBox="0 0 16 16" width="12" height="12">
                        <path
                            d="M6.2 11.6 2.6 8l1.1-1.1 2.5 2.5 5.1-5.1L12.4 5.4z"
                            fill="currentColor"
                        />
                    </svg>
                ) : (
                    <svg viewBox="0 0 16 16" width="12" height="12">
                        <path
                            d="M5 2h6a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zM3 5H2a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1v-1H3z"
                            fill="currentColor"
                        />
                    </svg>
                )}
            </span>
            {copied && (
                <span className="portal-sr-only">{t('console.audit.copy.copiedSrText')}</span>
            )}
        </button>
    );
}
